import { createRolloutSelectors, isFeatureEnabled } from '@lumapps/features';
import { isSuperAdmin, isUserAllowed } from '@lumapps/permissions';
import { createSelector } from '@lumapps/redux/reselect';

import {
    AGENT_HUB_FEATURE_TOKEN,
    AGENT_HUB_EARLY_ACCESS_FEATURE_TOKEN,
    AGENT_HUB_DEVELOPMENT_FEATURE_TOKEN,
    AI_AGENTS_STUDIO_FF,
} from '../constants';

/**
 * Agent Hub rollout selectors following the inclusive hierarchy model.
 * - isGA: Production-ready features
 * - isEarlyAccess: Stable pre-release features (Early-Access + Development environments)
 * - isDevelopment: Unstable, in-progress features (Development environments only)
 */
export const agentHubRollout = createRolloutSelectors({
    ga: AGENT_HUB_FEATURE_TOKEN,
    earlyAccess: AGENT_HUB_EARLY_ACCESS_FEATURE_TOKEN,
    development: AGENT_HUB_DEVELOPMENT_FEATURE_TOKEN,
});

/**
 * Retrieves whether the current user is allowed to read/use agents in the front office.
 *
 * Based on the `canReadAgent` permission returned by the backend at front-init.
 * Instance super-admins are allowed regardless (handled by `isUserAllowed`).
 */
export const canReadAgentSelector = isUserAllowed('canReadAgent');

/**
 * Retrieves whether the agent library (top bar entry + `/agent-library` route) should be
 * accessible to the current user: the Agent Hub early-access FF must be on AND the user must
 * have the `canReadAgent` permission.
 */
export const canAccessAgentLibrarySelector = createSelector(
    agentHubRollout.isGA,
    canReadAgentSelector,
    (isEnabled, hasAgentReadPermission) => isEnabled && hasAgentReadPermission,
);

/**
 * Retrieves whether the `ai-agents-studio` FF has been enabled
 *
 * This FF enables the "agent creation" feature on the product.
 */
export const agentStudioEnabledSelector = isFeatureEnabled(AI_AGENTS_STUDIO_FF);

/**
 * Retrieves whether the user can access the agent hub admin.
 */
export const canAccessAgentHubAdminSelector = createSelector(
    agentHubRollout.isGA,
    isSuperAdmin,
    (agentHubEnabled, userIsSuperAdmin) => userIsSuperAdmin && agentHubEnabled,
);

/** Retrieves whether the user can access the agent creation page */
export const canAccessAgentCreationAdminSelector = createSelector(
    canAccessAgentHubAdminSelector,
    agentStudioEnabledSelector,
    (canAccessToAdmin, isAgentStudioEnabled) => canAccessToAdmin && isAgentStudioEnabled,
);
